/* eslint-disable no-undef */
import apiService from "@/../api/rest";
import Payment from "@/models/payment";
import OrderDetails from "@/models/order-details";
import CheckoutService from "@/../api/services/CheckoutService";
import CheckoutAPService from "@/../api/services/CheckoutAPService";
import CheckoutFPAPService from "@/../api/services/CheckoutFPAPService";
import { processPpiReductionsMAPI, processPpiReductionsOAPI, removePpiReductionsMAPI } from "@/../api/services/PpiService";
import { getCardLastFour } from "../../../../utils/payments.ts";
import { mapCredentialInfo, mapPosInfo, mapOrderInfo } from "@appetize/iw-common/mappers/ppiMappers";
import {
  DECLINED_AP_PAYMENT,
  ERROR_PROCESSING_AP_PAYMENT,
  PAY_TYPE_APPLE,
  ERROR_PROCESSING_PAYMENT,
  SENTRY_ERRORS,
  ELAVON_GATEWAY_ID,
  FREEDOMPAY_GATEWAY_ID
} from "@/../app-constants";
import { getRandomInvoiceNumber, roundOffByBankApproach } from "@/utils";
import { trackPurchase } from "@/utils/pagesTrack";
import { mapElavonRequest, mapElavonAPRequest } from "../../../../../api/mappers/DebitRequestMapper";
import { mapElavonResponse, mapElavonAPResponse } from "../../../../../api/mappers/DebitResponseMapper";
import { mapAPRequest } from "../../../../../api/mappers/APRequestMapper";
import { OAPI_API_TYPE } from "@/utils";
import APConfirmationModal from "@/components/modal/APConfirmationModal/APConfirmationModal";
import { mapFPResponseMapper } from "../../../../../api/mappers/FPResponseMapper";
import errorLoggerAdapter from "@/adapters/errorLoggerAdapter";

export const getDefaultState = () => {
  return {
    initElavonAP: false,
    initAPCalled: false,
    checkoutErrors: []
  };
};

// initial state
export const state = getDefaultState();

// getters
const getters = {
  getFPInitDataForAP(state, getters, rootState, rootGetters) {
    const amount = `${rootGetters["cart/getAppliedAmenities"]?.balance}`;
    const vendorName = rootGetters["vendor/getActiveVendor"]?.name || "";
    return mapAPRequest({
      amount,
      vendorName
    });
  },
  getCheckoutErrors(state) {
    return state.checkoutErrors;
  },
  getInitElavonAP(state) {
    return state.initElavonAP;
  },
  getInitAPCalled(state) {
    return state.initAPCalled;
  },
  getCartItemsForCheckout(state, getters) {
    // added this to fix the JS error when page first loads, it was disabling the vuex dev tools.
    if (!getters || !getters.getDiscountedCartItems) return [];

    return getters.getDiscountedCartItems;
  },
  getFormattedCartItems(state, getters) {
    return getters.getAvailableCartItems.map((cartItem) => {
      return {
        item: {
          id: parseInt(cartItem.item.id),
          cost: cartItem.item.cost * cartItem.quantity,
          unitCost: +cartItem.item.cost,
          notes: cartItem.specialInstructions,
          originalCost: +cartItem.item.cost
        },
        additionalItems: getters.getAdditionalItems(cartItem),
        modifiers: cartItem.modifiers,
        taxes: [],
        quantity: cartItem.quantity
      };
    });
  },
  getPaymentsForCheckout(state, getters, rootState, rootGetters) {
    return (paymentParams) => {
      if (!paymentParams.payments || (paymentParams.payments && paymentParams.payments.length === 0)) {
        paymentParams.payments = [
          {
            cardEncoded: 0,
            cardNumber: "",
            cardholderFullname: "",
            expDate: ""
          }
        ];
      }
      return new Payment(paymentParams, getters, rootGetters);
    };
  }
};

// actions
const actions = {
  /**
   * Loops over applied reductions and call PPI useReductions enpdoint
   * @param rootGetters
   * @returns {Promise<*>}
   */
  async usePpiReductions({ getters, rootGetters }) {
    const result = [];
    const accountId = rootGetters["vendor/getActiveVendor"].defaultMobileOrderingAccountID;
    const OAPICart = rootGetters["cart/getOAPICart"];
    const appliedDiscountTags = rootGetters["cart/getOAPICartDiscountsTags"];
    const appliedReductions = getters.getFlatReductions.filter((reduction) =>
      appliedDiscountTags.find((tag) => `${reduction.reductionId}` === tag)
    );

    // We use a for of instead of a Promise.all because the call to /useReduction must be in order/sequential

    for (const reduction of appliedReductions) {
      const { selectedTender, card } = reduction;
      try {
        const response = await apiService.useReductions(accountId, selectedTender.paymentType, selectedTender.subpaymentType, {
          credentialInfo: mapCredentialInfo(card, selectedTender.selection, selectedTender.selectionType),
          posInfo: mapPosInfo(
            +rootGetters["venue/getActiveVenue"],
            +rootGetters["vendor/getActiveVendor"].id,
            +rootGetters["vendor/getActiveVendor"].defaultMobileOrderingAccountID
          ),
          orderInfo: mapOrderInfo(
            OAPICart.deviceOrderUuid,
            OAPICart.total,
            OAPICart.subtotal,
            OAPICart.discounts,
            OAPICart.taxTotal,
            selectedTender?.currency ?? "",
            rootGetters["cart/getCartItemsForCheckout"],
            rootGetters["cart/getPpiPayments"],
            rootGetters["cart/getPpiRedemptions"]
          ),
          reductions: [reduction]
        });
        result.push(response.data);
      } catch (e) {
        // AC requirement: we ignore the errors coming from
        // useReductions endpoint and let the flow continue
        errorLoggerAdapter.createIssue(SENTRY_ERRORS.ERROR_OAPI_PROCESS_REDUCTION, "checkout", "usePpiReductions", {
          description: e
        });
      }
    }

    return result;
  },

  async checkoutCreditCard({ getters, rootGetters, dispatch, commit }, paymentParams) {
    const isOAPI = rootGetters["venueSettings/APIType"] === OAPI_API_TYPE;
    const payment = Object.assign({}, paymentParams);
    let processedPayments;
    if (isOAPI) {
      processedPayments = getters.getProcessedAmenitiesApplied;
      payment.payments = processedPayments?.resultPayments || [];
      await dispatch("usePpiReductions");
    }

    const payload = getters.getPaymentsForCheckout(payment);
    if (isOAPI) {
      payload.reportedAmenities = processedPayments?.reportedAmenities || [];
      payload.debitExternalData = paymentParams.debitExternalData;
    }

    return CheckoutService.post(rootGetters, dispatch, payload, processedPayments).then(
      (response) => {
        const deviceOrderUuid = getters.getOAPICart?.deviceOrderUuid;
        const order = new OrderDetails(
          {
            deviceOrderUuid,
            ...response.data
          },
          payload.payments
        );
        //do not reject a checkout if trackPurchase throws an error
        try {
          trackPurchase(order, rootGetters["cart/getCheckoutParams"].cartItems);
        } catch (error) {
          errorLoggerAdapter.createIssue(
            SENTRY_ERRORS.ERROR_TRACKING_GTM_PURCHASE_EVENT,
            "checkoutError",
            "checkoutCreditCard",
            {},
            error
          );
        }

        commit("cart/setCheckoutOrder", order, { root: true });
        dispatch("clearShoppingCart");
        return Promise.resolve({ order, data: response.data });
      },
      (reject) => {
        //Don't log declined transactions due to invalid card numbers
        if (!reject?.response?.data?.success) {
          errorLoggerAdapter.createIssue(SENTRY_ERRORS.ERROR_CHECKOUT, "checkoutError", "checkoutCreditCard", {}, reject);
        }
        dispatch("voidAmenityPayments");
        return Promise.reject(reject);
      }
    );
  },

  processPpiReductions({ getters, rootGetters, dispatch }) {
    if (rootGetters["venueSettings/APIType"] === OAPI_API_TYPE) return processPpiReductionsOAPI(getters, rootGetters, dispatch);
    return processPpiReductionsMAPI(rootGetters, dispatch);
  },

  /**
   * Performs logic for a reduction removal, for OAPI it calls processPpiReductions
   * for MAPI it calls removePpiReductionsMAPI
   * @param getters
   * @param rootGetters
   * @param dispatch
   * @returns {{ Promise }}
   */
  removePpiReductions({ getters, rootGetters, dispatch }) {
    if (rootGetters["venueSettings/APIType"] === OAPI_API_TYPE) return processPpiReductionsOAPI(getters, rootGetters, dispatch);
    return removePpiReductionsMAPI(rootGetters, dispatch);
  },
  /**
   * Payment session initializer router will run different initializations based on the current gatewayId
   */
  async initPaymentSession({ rootGetters, dispatch }, payload) {
    const gatewayId = rootGetters["vendor/tokenizedGatewayId"];
    let response = null;

    switch (gatewayId) {
      case FREEDOMPAY_GATEWAY_ID:
        response = dispatch("initFreedomPay", payload);
        break;

      case ELAVON_GATEWAY_ID:
      default:
        response = dispatch("initElavon", payload);
        break;
    }
    return response;
  },

  /**
   * Payment session initializer router will run different re-initializations based on the current gatewayId
   */
  async reInitPaymentSession({ rootGetters, dispatch }, payload) {
    const gatewayId = rootGetters["vendor/tokenizedGatewayId"];
    let response = null;

    switch (gatewayId) {
      case FREEDOMPAY_GATEWAY_ID:
        response = dispatch("initFreedomPay", payload);
        break;

      case ELAVON_GATEWAY_ID:
      default:
        //dont do anything for elavon and as default -- particularly for Apple Pay
        break;
    }
    return response;
  },

  /**
   * FreedomPay specific initialization code.
   *
   * @param {*} selectedPayType
   * @param {*} checkoutPayload
   * @param {*} addErrorCallback
   * @returns Promise<any>
   */
  async initFreedomPay({ dispatch, rootGetters }, { selectedPayType, checkoutPayload, addErrorCallback }) {
    try {
      return await dispatch(
        "payments/getPaymentSession",
        { amount: rootGetters["cart/getAppliedAmenities"].balance || 0, payType: selectedPayType },
        { root: true }
      );
    } catch (e) {
      addErrorCallback(ERROR_PROCESSING_PAYMENT);
    }
  },

  /**
   * Elavon AP specific initialization code:
   *
   * This initializes elavon AP which adds the button listener for AP final checkout. If the amount charged changes,
   * user uses a tender for partial payment, the page needs to be refreshed to reflect that change.
   *
   * Page refresh logic:
   *
   * If the user navigates back to the cart to make a change, checkout.js requires a page refresh
   * to allow for cart changes, without refresh it will not call to init AP again. AP checkout will fail
   * with spinner and softlock.
   *
   * Once the initElavonAP method is called, it will set store initElavonAP to true, if the user navigates back to
   * cart and then comes back to checkout, it will check if AP is enabled, and initElavonAP is true, it will refresh
   * the page, ensuring checkout.js initAP will work properly.
   */
  async initElavon(
    { getters, rootGetters, commit, dispatch },
    { selectedPayType, checkoutPayload, successCallback, addErrorCallback }
  ) {
    const isOAPI = rootGetters["venueSettings/APIType"] === OAPI_API_TYPE;
    const balance = rootGetters["cart/getAppliedAmenities"]?.balance;

    if (selectedPayType === PAY_TYPE_APPLE && isOAPI && balance > 0) {
      if (getters.getInitElavonAP) {
        commit("setInitElavonAP", false);
        location.reload();
        return;
      }

      try {
        await dispatch("payments/getPaymentSession", { amount: balance, payType: PAY_TYPE_APPLE }, { root: true });
        commit("setInitElavonAP", true);

        const debitExternalData = await dispatch("checkoutElavonAP");

        /**
         * The following code will be executed after the Elavon AP button has been clicked.
         * checkouElavonAP isn't resolving until that click happens
         */
        checkoutPayload.debitExternalData = debitExternalData;
        const { order } = await dispatch("checkoutCreditCard", checkoutPayload);
        successCallback(order);
      } catch (error) {
        errorLoggerAdapter.createIssue(
          SENTRY_ERRORS.ERROR_OAPI_ELAVON_AP_CHECKOUT,
          "checkoutError",
          "checkout.actions.initElavon.catch",
          {
            description: error.toString()
          },
          error
        );

        if (error && typeof error === "string" && error.length) {
          addErrorCallback(ERROR_PROCESSING_AP_PAYMENT);
          commit("setCheckoutErrors", [ERROR_PROCESSING_AP_PAYMENT]);
          commit("setInitElavonAP", false);
          location.reload();
        }
      }
    }
  },

  /**
   * OAPI Apple pay router will run different actions based on gateway id
   */
  async checkoutOAPIApplePay({ rootGetters, dispatch }, payload) {
    const gatewayId = rootGetters["vendor/tokenizedGatewayId"];
    let response = null;

    switch (gatewayId) {
      case FREEDOMPAY_GATEWAY_ID:
        response = dispatch("checkoutFPApplePay", payload);
        break;

      case ELAVON_GATEWAY_ID:
      default:
        response = dispatch("checkoutElavonApplePay");
        break;
    }
  },

  /**
   * This is called from checkout provided AP is selected and is OAPI. Call the
   * click event on a hidden button that checkout.js has attached a click listener to generate and ApplePaySession and
   * start the session.
   */
  processElavonAPTransaction() {
    document.getElementById("checkoutAPBtn").click();
  },

  /**
   * Elavon AP checkout.
   * If it's a split payment, it will show a modal to capture the users input. An ApplePaySession creation needs to
   * happen in a short time frame after the user actually hits a button. This solution allows us to run process if its
   * a split payment, then recapture the users input to create the AP session.
   * @link: https://developer.apple.com/documentation/apple_pay_on_the_web/apple_pay_js_api/creating_an_apple_pay_session
   */
  async checkoutElavonApplePay({ rootGetters, dispatch }) {
    const balance = rootGetters["cart/getAppliedAmenities"]?.balance;
    dispatch("showAPModal", {
      balance,
      payload: {},
      processTransaction: actions.processElavonAPTransaction
    });
  },

  /**
   * FreedomPay AP checkout.
   */
  processFPApplePayTransaction(payload) {
    payload.commit("spinner/requestStart", null, { root: true });
    const checkoutFPAPService = new CheckoutFPAPService(payload, payload.dispatch, payload.commit);
    checkoutFPAPService.init();
  },
  async checkoutFPApplePay({ rootGetters, commit, dispatch }, paymentPayload) {
    const payload = Object.assign(paymentPayload, { commit, dispatch });
    const balance = rootGetters["cart/getAppliedAmenities"]?.balance;

    dispatch("showAPModal", {
      balance,
      payload,
      processTransaction: actions.processFPApplePayTransaction
    });
  },

  /**
   * This is continue callback for AP modal.
   * @param payload
   */
  processMAPIApplePayTransaction(payload) {
    payload.commit("spinner/requestStart", null, { root: true });
    payload.dispatch("checkoutApplePay", payload);
  },
  async checkoutMAPIApplePay({ rootGetters, commit, dispatch }, paymentPayload) {
    const balance = rootGetters["cart/getAppliedAmenities"]?.balance;
    const payload = Object.assign(paymentPayload, { commit, dispatch });
    dispatch("showAPModal", { balance, payload, processTransaction: actions.processMAPIApplePayTransaction });
  },

  /**
   * Shows AP modal, this is for all AP payment flows, used to catch user input incase of timeout.
   * @param commit
   * @param balance
   * @param payload
   * @param processTransaction
   * @param closeModal
   */
  showAPModal({ commit, dispatch }, { balance, payload, processTransaction }) {
    const closeModal = () => {
      dispatch("modals/hideModal", null, { root: true });
    };

    const processApplePayTransaction = () => {
      if (processTransaction) processTransaction(payload);
      dispatch("modals/hideModal", null, { root: true });
    };

    dispatch(
      "modals/showModal",
      {
        modalComp: APConfirmationModal,
        props: {
          message: `Balance of $${roundOffByBankApproach(parseFloat(balance)).toFixed(
            2
          )} will be deducted from your Apple Pay account`,
          onContinue: processApplePayTransaction,
          onClose: closeModal
        },
        params: {
          name: "APConfirmationModal",
          height: "auto",
          width: 500,
          alwaysShowBoarderRadius: true,
          adaptive: true,
          scrollable: true,
          clickToClose: false,
          focusTrap: true
        }
      },
      { root: true }
    );
  },

  async processCreditCardCapture({ rootGetters, dispatch }, payload) {
    try {
      const gatewayId = rootGetters["vendor/tokenizedGatewayId"];
      let response = null;
      let cardToken = null;
      switch (gatewayId) {
        case FREEDOMPAY_GATEWAY_ID:
          if (payload.creditCardData && !payload.creditCardData.cardHolder) {
            const userData = rootGetters["user/getUserData"];
            if (userData?.profile) {
              payload.creditCardData.cardHolder = `${userData.profile.firstName} ${userData.profile.lastName}`;
            }
          }
          response = await dispatch("checkoutFP", payload);
          cardToken = response?.FreewayResponse?.tokenInformation?.token || rootGetters["payments/getFPPaymentKey"];
          break;
        case ELAVON_GATEWAY_ID:
        default:
          response = await dispatch("checkoutElavon", payload.creditCardData);
          cardToken = response.cardToken;
          break;
      }

      if (payload.saveCardToken && cardToken) {
        const { cardNumber, expDate, cardHolder } = payload.creditCardData;
        dispatch(
          "user/saveCreditCardToken",
          {
            gatewayId: gatewayId,
            cardToken: cardToken,
            cardLast4: getCardLastFour(cardNumber),
            cardExpDate: expDate,
            cardHolderName: cardHolder
          },
          { root: true }
        );
      }

      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  },

  checkoutElavon({ rootGetters, commit, dispatch }, params) {
    const token = rootGetters["payments/getPaymentSessionToken"];
    const invoiceNumber = getRandomInvoiceNumber();
    const paymentData = mapElavonRequest({ token, invoiceNumber, ...params });
    return new Promise((resolve, reject) => {
      commit("spinner/requestStart", null, { root: true });
      ConvergeEmbeddedPayment.pay(paymentData, {
        onError: function (error) {
          errorLoggerAdapter.createIssue(SENTRY_ERRORS.ERROR_OAPI_ELAVON_CC_CHECKOUT, "checkoutError", "checkoutElavon", {
            description: JSON.stringify(error)
          });
          dispatch("voidAmenityPayments");
          commit("spinner/requestStop", null, { root: true });
          reject(error);
        },
        onDeclined: async function (response) {
          dispatch("voidAmenityPayments");
          commit("spinner/requestStop", null, { root: true });
          reject(response);
        },
        onApproval: function (response) {
          commit("spinner/requestStop", null, { root: true });
          resolve(mapElavonResponse(response));
        }
      });
    });
  },

  checkoutElavonAP({ rootGetters, commit, dispatch }) {
    const token = rootGetters["payments/getPaymentSessionToken"];
    const invoiceNumber = getRandomInvoiceNumber();

    const paymentData = mapElavonAPRequest(invoiceNumber, token);
    return new Promise((resolve, reject) => {
      ConvergeEmbeddedPayment.initApplePay("checkoutAPBtn", paymentData, {
        onError: async function (error) {
          errorLoggerAdapter.createIssue(
            SENTRY_ERRORS.ERROR_OAPI_ELAVON_AP_CHECKOUT,
            "checkoutElavonAP",
            "store.checkout.actions.checkoutElavonAP.onError",
            {
              description: error.toString()
            },
            error
          );
          await dispatch("voidAmenityPayments");
          commit("spinner/requestStop", null, { root: true });
          reject(ERROR_PROCESSING_AP_PAYMENT);
        },
        onDeclined: async function (response) {
          await dispatch("voidAmenityPayments");
          commit("spinner/requestStop", null, { root: true });
          reject(DECLINED_AP_PAYMENT);
        },
        onCancelled: async function (response) {
          await dispatch("voidAmenityPayments");
          commit("spinner/requestStop", null, { root: true });
          reject("Apple Pay checkout cancelled");
        },
        onApproval: function (response) {
          commit("spinner/requestStop", null, { root: true });
          resolve(mapElavonAPResponse(response, invoiceNumber));
        }
      });
    });
  },

  checkoutApplePay({ dispatch }, payload) {
    const checkoutAPService = new CheckoutAPService(payload, dispatch);
    checkoutAPService.init();
  },

  async checkoutFP({ dispatch }, payload) {
    try {
      return await dispatch("payments/payPayment", payload, { root: true });
    } catch (e) {
      if (e?.data?.code !== 600) {
        errorLoggerAdapter.createIssue(SENTRY_ERRORS.ERROR_OAPI_FP_PAY, "checkoutError", "checkoutFP", {}, e);
      }
      dispatch("voidAmenityPayments");
      return Promise.reject(e);
    }
  },

  /**
   * connectCheckout router will run different checkout methods based on the current gatewayId
   */
  async connectCheckout({ rootGetters, dispatch }, data) {
    const gatewayId = rootGetters["vendor/tokenizedGatewayId"];
    const balance = rootGetters["cart/getAppliedAmenities"].balance;
    const APIType = rootGetters["venueSettings/APIType"];

    const payload = {
      ...data,
      balance,
      isOAPI: APIType === OAPI_API_TYPE
    };

    switch (gatewayId) {
      case FREEDOMPAY_GATEWAY_ID:
        await dispatch("connectCheckoutFP", payload);
        break;

      case ELAVON_GATEWAY_ID:
      default:
        await dispatch("connectCheckoutElavon", payload);
        break;
    }
  },

  async connectCheckoutFP(
    { rootGetters, dispatch, getters },
    { balance, isOAPI, processCCPayload, paymentPayload, sessionKey, successCallback, errorCallback, catchCallback }
  ) {
    try {
      const invoiceNumber = getRandomInvoiceNumber();
      processCCPayload.invoiceNumber = invoiceNumber;
      processCCPayload.payment = paymentPayload;

      if (isOAPI && balance !== 0) {
        const debitExternalData = await dispatch("processCreditCardCapture", processCCPayload);
        paymentPayload.debitExternalData = mapFPResponseMapper(debitExternalData, invoiceNumber);
      }

      const { order, data } = await dispatch("checkoutCreditCard", paymentPayload);
      if (data && !data.success) {
        errorCallback(data);
        return;
      }

      successCallback(order);
    } catch (e) {
      catchCallback(e);
    }
  },

  async connectCheckoutElavon(
    { dispatch },
    { balance, isOAPI, processCCPayload, paymentPayload, successCallback, errorCallback, catchCallback }
  ) {
    try {
      if (isOAPI && balance !== 0) {
        await dispatch("payments/getPaymentSession", { amount: balance }, { root: true });
        const debitExternalData = await dispatch("processCreditCardCapture", processCCPayload);
        paymentPayload.debitExternalData = debitExternalData;
      }

      const { order, data } = await dispatch("checkoutCreditCard", paymentPayload);

      if (data && !data.success) {
        errorCallback(ERROR_PROCESSING_PAYMENT);
        return;
      }

      successCallback(order);
    } catch (e) {
      dispatch("voidAmenityPayments");
      catchCallback(ERROR_PROCESSING_PAYMENT);
    }
  }
};

// mutations
const mutations = {
  setInitElavonAP(state, value) {
    state.initElavonAP = value;
  },
  setInitAPCalled(state, value) {
    state.initAPCalled = value;
  },
  addCheckoutError(state, error) {
    state.checkoutErrors.push(error);
  },
  setCheckoutErrors(state, errors) {
    state.checkoutErrors = errors;
  },
  resetCheckoutErrors(state) {
    state.checkoutErrors = [];
  },
  resetCheckoutState(state) {
    Object.assign(state, getDefaultState());
  }
};

export default {
  namespaced: false,
  state,
  getters,
  actions,
  mutations
};
